// Home "Today" tile (EXCEL_SPRINTS PR5, Thrust 1b) — the daily driver.
//
// One prioritized read of the hunt: jobs worth applying to, applications that
// have gone stale, interviews coming up, and drafts to finish. Each row deep-
// links to where the work happens. Hides itself when there's nothing to do.

import { Link } from "react-router";
import { CalendarClock, ClipboardList, Handshake, MailQuestionMark, Sparkles, TimerReset } from "lucide-react";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Pill } from "../ui/Pill";
import { Skeleton } from "../ui/Skeleton";
import { useHuntAgenda, type InProgressApplication } from "../../lib/hunt";

export function TodayTile() {
  const agenda = useHuntAgenda();
  const data = agenda.data;

  if (agenda.isLoading) {
    return (
      <Card>
        <CardHeader title="Today" />
        <CardBody>
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="mt-2 h-4 w-1/2" />
        </CardBody>
      </Card>
    );
  }

  if (agenda.error || !data) return null;

  const empty =
    data.summary.shortlist === 0 &&
    data.summary.stuck === 0 &&
    data.summary.upcoming_interviews === 0 &&
    data.summary.in_progress === 0 &&
    data.summary.approved_unsubmitted === 0 &&
    (data.summary.warm_touches_due ?? 0) === 0 &&
    (data.summary.recruiter_replies_due ?? 0) === 0;
  if (empty) return null;

  return (
    <Card>
      <CardHeader
        title="Today"
        description="Your prioritized next actions across the hunt."
      />
      <CardBody className="space-y-4">
        {data.upcoming_interviews.length > 0 ? (
          <Section
            icon={<CalendarClock size={14} aria-hidden />}
            title="Upcoming interviews"
          >
            {data.upcoming_interviews.map((iv) => (
              <Row
                key={iv.application_id}
                to={`/applications/${iv.application_id}/prep`}
                primary={`${iv.role} · ${iv.company_name}`}
                meta={formatWhen(iv.scheduled_at)}
              />
            ))}
          </Section>
        ) : null}

        {data.recruiter_replies_due && data.recruiter_replies_due.length > 0 ? (
          <Section icon={<MailQuestionMark size={14} aria-hidden />} title="Recruiters: reply within 24h">
            {data.recruiter_replies_due.map((r) => (
              <Row
                key={r.message_id}
                to="/contacts"
                primary={
                  r.contact
                    ? `Reply to ${r.contact.full_name}${r.contact.company_name ? ` · ${r.contact.company_name}` : ""}`
                    : (r.subject ?? "Recruiter message")
                }
                meta={r.overdue ? "Overdue" : `Due ${formatDue(r.due_at)}`}
              />
            ))}
          </Section>
        ) : null}

        {data.approved_unsubmitted.length > 0 ? (
          <Section icon={<ClipboardList size={14} aria-hidden />} title="Approved: submit next">
            {data.approved_unsubmitted.map((a) => (
              <Row
                key={a.application_id}
                to={`/review/${a.application_id}`}
                primary={`${a.role} · ${a.company_name}`}
                meta={a.listing_active === false ? "Approved · listing closed" : packetMeta(a, "Approved")}
              />
            ))}
          </Section>
        ) : null}

        {data.warm_touches_due && data.warm_touches_due.length > 0 ? (
          <Section icon={<Handshake size={14} aria-hidden />} title="Warm touches due">
            {data.warm_touches_due.map((w) => (
              <Row
                key={w.company_key}
                to="/contacts"
                primary={
                  w.suggested_contact
                    ? `Message ${w.suggested_contact.full_name} · ${w.company_name}`
                    : `Name someone at ${w.company_name}`
                }
                meta={`${w.applications.length} open${w.tier === 1 ? " · Tier 1" : ""}`}
              />
            ))}
          </Section>
        ) : null}

        {data.in_progress.length > 0 ? (
          <Section icon={<ClipboardList size={14} aria-hidden />} title="Review & approve">
            {data.in_progress.map((a) => (
              <Row
                key={a.application_id}
                to={`/review/${a.application_id}`}
                primary={`${a.role} · ${a.company_name}`}
                meta={packetMeta(a, "In preparing")}
              />
            ))}
          </Section>
        ) : null}

        {data.stuck.length > 0 ? (
          <Section icon={<TimerReset size={14} aria-hidden />} title="Follow up (gone quiet)">
            {data.stuck.map((s) => (
              <Row
                key={s.application_id}
                to={`/applications/${s.application_id}/apply`}
                primary={`${s.role} · ${s.company_name}`}
                meta={`${s.age_days}d in applied`}
              />
            ))}
          </Section>
        ) : null}

        {data.shortlist.length > 0 ? (
          <Section
            icon={<Sparkles size={14} aria-hidden />}
            title="Worth applying to"
            action={
              data.shortlist_source === "cold_start" ? (
                <Pill tone="accent">from your profile</Pill>
              ) : null
            }
          >
            {data.shortlist.map((j) => (
              <a
                key={`${j.company_id}:${j.job_id}`}
                href={j.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 py-1.5 text-sm hover:underline"
              >
                <span className="min-w-0 truncate text-neutral-200">
                  {j.title} · {j.company_name}
                </span>
                {j.score != null ? (
                  <span className="shrink-0 text-xs text-neutral-500">fit {j.score}</span>
                ) : null}
              </a>
            ))}
          </Section>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Section({
  icon,
  title,
  action,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-neutral-400">
          <span className="text-neutral-500">{icon}</span>
          {title}
        </h3>
        {action}
      </div>
      <div className="divide-y divide-neutral-800/60">{children}</div>
    </div>
  );
}

function Row({ to, primary, meta }: { to: string; primary: string; meta: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-3 py-1.5 text-sm hover:underline"
    >
      <span className="min-w-0 truncate text-neutral-200">{primary}</span>
      <span className="shrink-0 text-xs text-neutral-500">{meta}</span>
    </Link>
  );
}

function formatDue(ts: number): string {
  const hours = Math.max(0, Math.round((ts - Date.now()) / 3_600_000));
  return hours <= 1 ? "within the hour" : `in ${hours}h`;
}

function formatWhen(ts: number): string {
  const d = new Date(ts);
  const now = Date.now();
  const days = Math.round((ts - now) / 86_400_000);
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (days <= 0) return `${date} (today)`;
  if (days === 1) return `${date} (tomorrow)`;
  return `${date} (${days}d)`;
}

// "fit 2.9/4 · auto-fill": what makes one packet worth reviewing before
// another. The list already arrives best-fit first.
function packetMeta(a: InProgressApplication, fallback: string): string {
  const parts: string[] = [];
  if (a.expected_fit != null) parts.push(`fit ${a.expected_fit.toFixed(1)}/4`);
  if (a.autofill) parts.push("auto-fill");
  return parts.length > 0 ? parts.join(" · ") : fallback;
}
